import IconExcel from "@/assets/icones/excel.svg?react";
import IconImg from "@/assets/icones/images.svg?react";
import IconPdf from "@/assets/icones/pdf.svg?react";
import IconWord from "@/assets/icones/word.svg?react";
import ReactBreadcrumb from "@/components/breadcrumb/Breadcrumb";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { LoadingSpinner } from "@/components/ui/loadingSpinner";
import DocumentCard from "@/documents/components/DocumentCard";
import DocumentList from "@/documents/components/DocumentList";
import FilterDocument from "@/documents/components/FilterDocument";
import { useFetchDataV2 } from "@/hooks/useFetchDataV2";
import SwitchList from "@/project-owner/components/SwitchList";
import { Documents, TreeRoot } from "@/types";
import { Eye, FolderOpen } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams, useSearchParams } from "react-router-dom";
/* eslint-disable react/react-in-jsx-scope */

const REFERENCE_DOC_LABELS = ["Dossier de projet", "Contractuel"];

const getDocumentSubtype = (doc: Record<string, unknown>): number | null => {
  const candidate = doc.type;
  const numeric = Number(candidate);
  return Number.isNaN(numeric) ? null : numeric;
};

const findCategoryInfo = (typesDocs: any[], typeValue: number | null) => {
  if (typeValue === null) return null;

  const parent = typesDocs.find((item: any) =>
    item.children?.some((child: any) => Number(child.value) === typeValue),
  );
  const child = parent?.children?.find(
    (item: any) => Number(item.value) === typeValue,
  );

  if (!parent || !child) return null;

  return {
    category: parent.label,
    idCategory: parent.value,
    subcategory: child.label,
    idSubcategory: child.value,
  };
};

const ProjectDocument = () => {
  const {
    data: treeFinancer,
    loading: loadingTreeFinancer,
    error: errorTreeFinancer,
  } = useFetchDataV2<TreeRoot[]>(
    `${import.meta.env.VITE_API_ENDPOINT}/tree/financer`,
    `tree-financer`,
    1000 * 60 * 60,
  );
  const queryParams = new URLSearchParams(window.location.search);
  const { t } = useTranslation("dashboard", {
    keyPrefix: "details",
  });
  const { firstname } = useParams();
  const [view, setView] = useState<"list" | "card">("card");
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const [typesDocs, setTypesDocs] = useState([]);
  const { data: dataDocuments, loading: loadingDocuments } = useFetchDataV2<
    Documents[]
  >(
    `${import.meta.env.VITE_API_ENDPOINT}/docs/financer?project=${id}`,
    `docs-financer`,
    1000 * 60 * 60,
  );
  const [dataFiltered, setDataFiltered] = useState<Documents[]>([]);

  const [porteursList, setPorteursList] = useState<any[]>([]);

  useEffect(() => {
    if (treeFinancer && treeFinancer.length > 0) {
      const options = treeFinancer.map((item: TreeRoot) => {
        return {
          id: item.id,
          name: item.name,
        };
      });
      setPorteursList(options);
    }
  }, [treeFinancer]);
  useEffect(() => {
    const localStorageKey = "typesDocsCache";

    const cached = localStorage.getItem(localStorageKey);
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed)) {
          setTypesDocs(parsed);
          return; // Pas besoin de fetch
        }
      } catch (e) {
        console.warn(
          "Échec du parsing du cache localStorage, on refait un fetch",
        );
      }
    }

    // Sinon on fetch et on stocke
    fetch(`${import.meta.env.VITE_IP_API}/types-docs.json`)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        if (data) {
          setTypesDocs(data);
          localStorage.setItem(localStorageKey, JSON.stringify(data));
        }
      })
      .catch((error) => {
        console.error("Erreur lors du fetch :", error);
      });
  }, []);

  const optionsType = [
    {
      value: "document",
      label: "Documents",
      icon: <IconWord width={18} height={18} />,
    },
    {
      value: "spreadsheet",
      label: "Feuilles de calcul",
      icon: <IconExcel width={18} height={18} />,
    },
    {
      value: "image",
      label: "Photos et images",
      icon: <IconImg width={18} height={18} />,
    },
    {
      value: "pdf",
      label: "Fichiers PDF",
      icon: <IconPdf width={18} height={18} />,
    },
  ];

  const normalizedDocuments = useMemo(
    () =>
      dataFiltered.map((doc: any) => {
        const categoryInfo = findCategoryInfo(
          typesDocs,
          getDocumentSubtype(doc as Record<string, unknown>),
        ) || {
          category: doc.category,
          idCategory: doc.idCategory,
          subcategory: doc.subcategory,
          idSubcategory: doc.idSubcategory,
        };
        return {
          ...doc,
          ...categoryInfo,
        };
      }),
    [dataFiltered, typesDocs],
  );
  const trackingDocuments = useMemo(
    () =>
      normalizedDocuments.filter(
        (doc: any) => !REFERENCE_DOC_LABELS.includes(doc.category),
      ),
    [normalizedDocuments],
  );

  const referenceDocuments = useMemo(
    () =>
      normalizedDocuments.filter((doc: any) =>
        REFERENCE_DOC_LABELS.includes(doc.category),
      ),
    [normalizedDocuments],
  );


  return (
    <div>
      {loadingDocuments && !typesDocs ? (
        <LoadingSpinner />
      ) : (
        <FilterDocument
          typesDocs={typesDocs}
          optionsType={optionsType}
          dataDocuments={dataDocuments}
          setDataFiltered={setDataFiltered}
        />
      )}

      <div className="flex items-center gap-5 mb-4">
        <div className="py-[10px] flex-1 hover:no-underline group pl-4 font-semibold border-b border-b-white relative bg-[#fafafa] rounded-[9px] transition-all w-auto ">
          Mes fichiers
        </div>
        <div className="flex justify-end  ">
          <SwitchList setView={setView} view={view} />
        </div>
      </div>

      <div className="space-y-6">
        {referenceDocuments.length > 0 && (
          <section className="bg-card p-4 rounded-lg">
            <h2 className="mb-4 text-lg font-semibold text-foreground">
              Documents de référence
            </h2>
            {view === "list" ? (
              <DocumentList data={referenceDocuments} typesDocs={typesDocs} />
            ) : (
              <DocumentCard data={referenceDocuments} typesDocs={typesDocs} />
            )}
          </section>
        )}

        {trackingDocuments.length > 0 && (
          <section className="bg-card p-4 rounded-lg">
            <h2 className="mb-4 text-lg font-semibold text-foreground">
              Documents de suivi de projets
            </h2>
            {view === "list" ? (
              <DocumentList data={trackingDocuments} typesDocs={typesDocs} />
            ) : (
              <DocumentCard data={trackingDocuments} typesDocs={typesDocs} />
            )}
          </section>
        )}

        {referenceDocuments.length === 0 && trackingDocuments.length === 0 && (
          <div className="rounded-[20px] bg-card p-6 text-sm text-muted-foreground">
            Aucune donnée à afficher.
          </div>
        )}
      </div>
    </div >
  );
};

export default ProjectDocument;
